import { useMemo } from 'react';
import EditPositionPreview from '../EditPositionPreview';
import { formatEther, parseEther, parseUnits } from 'ethers/lib/utils';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import { calculatePnl, formatNonNegative } from '../../../../../helpers';
import useUserFee from '../../../../../hooks/web3/useUserFee';
import useTokenStats from '../../../../../hooks/web3/useTokenStats';
import { config } from '../../../../../config/config';
import { toBigNumber } from '../../../../../web3/utils';

type WithdrawPreviewProps = {
    userPosition: UserPositionRaw | undefined;
    amount: string;
};

const WithdrawPreview: React.FC<WithdrawPreviewProps> = ({ userPosition, amount }) => {
    const tokenStats = useTokenStats();
    const { userFee } = useUserFee();

    const pnlValues = userPosition
        ? calculatePnl(
              userPosition,
              tokenStats?.[userPosition.tradePair.chartSymbol]?.price
          )
        : undefined;

    // use toBigNumber here incase entryPrice is used which is in 24 decimals.
    const markPrice = useMemo(
        () =>
            tokenStats && userPosition && userPosition.tradePair
                ? toBigNumber(
                      tokenStats?.[userPosition.tradePair?.chartSymbol]?.price ||
                          userPosition?.entryPrice ||
                          '0'
                  )
                : toBigNumber(userPosition?.entryPrice || '0'),
        [tokenStats, userPosition]
    );

    const previewCollateral = useMemo(
        () => parseEther(userPosition?.margin || '0').sub(parseEther(amount || '0')),

        [userPosition?.margin, amount]
    );

    const closeFee = useMemo(
        () =>
            parseEther(amount || '0')
                .mul(userFee || 0)
                .div(100_00),
        [amount, userFee]
    );

    const previewleverage = useMemo(
        () =>
            parseEther(userPosition?.volume || '0')
                .mul(parseEther('1'))
                .div(previewCollateral),
        [userPosition?.volume, previewCollateral]
    );

    const previewBankruptcyPrice = useMemo(
        () =>
            parseUnits(userPosition?.entryPrice || '1', config.priceFeedDecimal)
                .mul(
                    parseUnits('1', config.priceFeedDecimal)[
                        userPosition?.isShort ? 'add' : 'sub'
                    ](
                        parseUnits('1', config.priceFeedDecimal)
                            .mul(parseUnits('1', config.priceFeedDecimal))
                            .div(
                                parseUnits(
                                    previewleverage.toString(),
                                    config.priceFeedDecimal - 18
                                )
                            )
                    )
                )
                .div(parseUnits('1', config.priceFeedDecimal)),
        [userPosition?.entryPrice, previewleverage, userPosition?.isShort]
    );
    return (
        <EditPositionPreview
            positionDetails={{
                tradePair: userPosition?.tradePair,
                bankruptcyPrice: {
                    value: userPosition?.bankruptcyPrice || '0',
                    previewValue: parseEther(amount || '0').gt(0)
                        ? formatNonNegative(
                              previewBankruptcyPrice,
                              config.priceFeedDecimal
                          )
                        : undefined,
                },
                collateral: {
                    value: userPosition?.margin || '0',
                    previewValue: parseEther(amount || '0').gt(0)
                        ? formatEther(previewCollateral)
                        : undefined,
                },
                entryPrice: {
                    value: userPosition?.entryPrice || '0',
                },
                fees: {
                    value: undefined,
                    previewValue: parseEther(amount || '0').gt(0)
                        ? formatEther(closeFee)
                        : undefined,
                },
                isShort: userPosition?.isShort ?? false, // should never happen, but just in case
                leverage: {
                    value: userPosition?.leverage || '0',
                    previewValue: parseEther(amount || '0').gt(0)
                        ? formatEther(previewleverage)
                        : undefined,
                },
                markPrice: {
                    value: formatEther(markPrice) || '0',
                },
                pnl: {
                    value: pnlValues?.pnl,
                },
                size: {
                    value: userPosition?.volume || '0',
                },
            }}
        />
    );
};

export default WithdrawPreview;
